/**
 * @typedef FormData
 * @property {object} values object containing current values
 * @property {object} errors object containing error states for each value
 * @property {object} msgs object containing error messages for each value
 * @property {boolean} hasChanges true if the user has modified the values
 * @property {boolean} hasError true if the there is an error
 **/

/**
 **/
function create(comp, options, extended) {
  let component = comp;
  let currentData = options.data;
  let currentForm = null;
  const {
    fields,
    validator,
    submitValidator,
    onValueChange,
    formKey = 'form'
  } = options;
  const {
    onRevert
  } = extended;
  function makeStruct(copy = {}) {
    const struct = {
      hasError: false,
      errorMsg: null,
      ...copy
    };
    struct.values = {
      ...(copy.values || {})
    };
    struct.errors = {
      ...(copy.errors || {})
    };
    struct.msgs = {
      ...(copy.msgs || {})
    };
    struct.submitErrors = {
      ...(copy.submitErrors || {})
    };
    struct.submitMsgs = {
      ...(copy.submitMsgs || {})
    };
    return struct;
  }
  function getValidator(name) {
    return fields?.[name]?.validator;
  }

  /**
   * Returns the current form data, either from the
   * component state or from the currentData
   * return {FormData}
   **/
  function getData(prevState) {
    if (prevState && prevState[formKey]) {
      return prevState[formKey];
    }
    if (hasChanges()) {
      return prevState == null ? component.state[formKey] : prevState[formKey];
    }
    return getDefaultForm();
  }
  function setComponent(comp) {
    component = comp;
  }

  /**
   * Sets the current (external) data. This is the default
   * state and the revert state
   **/
  function setDefaultData(data) {
    currentData = data || {};
    currentForm = null;
  }

  /**
   * Gets the current (external) data
   **/
  function getDefaultData() {
    return currentData;
  }
  function getDefaultForm() {
    if (currentForm == null) {
      currentForm = dataToForm(currentData || {});
    }
    return currentForm;
  }
  function changeFormData(name, value, action, validateOnly = false) {
    return (prevState, props) => {
      const cData = getData(prevState);
      const data = makeStruct(cData);
      switch (action) {
        case 'insert':
          if (data.values[name]) {
            data.values[name] = [...data.values[name], value];
          } else {
            data.values[name] = [value];
          }
          break;
        case 'remove':
          {
            const values = [...data.values[name]];
            values.splice(values.indexOf(value), 1);
            data.values[name] = values;
            break;
          }
        default:
          data.values[name] = value;
          break;
      }
      delete data.submitErrors[name]; // remove submit error on change
      delete data.submitMsgs[name];
      if (!validateOnly && onValueChange) {
        onValueChange(data.values, cData.values);
      }
      // Dont Lose submit errors
      const ns = dataToForm(data.values);
      ns.submitErrors = data.submitErrors;
      ns.hasChanges = true;
      if (validateOnly) {
        ns.values = prevState?.[formKey]?.values;
      }
      return {
        [formKey]: ns
      };
    };
  }
  function onInputChange(event) {
    let {
      value,
      data: name
    } = event;
    const {
      error
    } = event;
    const {
      action
    } = event;
    if (error) {
      return;
    }
    if (value === undefined && event.target) {
      const target = event.target;
      value = target && target.value;
      name = target && target.name;
    }
    if (name == null) {
      return;
    }
    const fData = getData();
    let allow = true;
    if (fields[name] && fields[name].active !== false) {
      // Checks to see if validator should even be allowed
      allow = ask(fields[name].allow, true, value, name, fData.values);
      if (allow === false) {
        component.setState(changeFormData(name, value, action, true));
        return;
      }
      // change the input and rerun validators      
      component.setState(changeFormData(name, value, action));
      return;
    }
    component.setState(changeFormData(name, value, action));
  }

  /**
   * Applys the error condition to the data
   * @param {*} data the data to modify
   * @param {*} name the name of the item to change
   * @param {*} err if false, this indicates and error. If a string it indicates
   * and error with a message
   * @param {*} isSubmit if true, this is for submit validators
   */
  function applyErrorState(data, name, err, isSubmit = false) {
    const errKey = isSubmit ? 'submitErrors' : 'errors';
    const msgKey = isSubmit ? 'submitMsgs' : 'msgs';
    if (err === false || typeof err === 'string') {
      data[errKey][name] = true; // eslint-disable-line no-param-reassign
      data[msgKey][name] = typeof err === 'string' ? err : null; // eslint-disable-line
      return true;
    }
    delete data[errKey][name]; // eslint-disable-line no-param-reassign
    delete data[msgKey][name]; // eslint-disable-line no-param-reassign
    return false;
  }
  function updateValidatorStates(data, isSubmit = false) {
    let hasError = false;
    if (fields) {
      Object.keys(fields).forEach(k => {
        if (fields[k].active === false) {
          return;
        }
        const vdr = isSubmit ? fields[k].submitValidator : fields[k].validator;
        const v = ask(vdr, true, data && data.values[k], k, data.values);
        hasError = applyErrorState(data, k, v, isSubmit) || hasError;
      });
    }
    return hasError;
  }
  function updateGeneralValidatorStates(data, isSubmit = false) {
    let hasError = false;
    const msgKey = isSubmit ? 'submitErrorMsg' : 'errorMsg';
    const vf = isSubmit ? submitValidator : validator;
    if (vf) {
      const v = vf(data.values) || {
        errors: []
      };
      if (v === true) {
        delete data[msgKey]; // eslint-disable-line
        data.hasError = false; // eslint-disable-line
        return hasError;
      }
      if (typeof v === 'string') {
        data[msgKey] = v; // eslint-disable-line
        data.hasError = true; // eslint-disable-line
        hasError = true;
      } else if (v != null && typeof v === 'object') {
        data[msgKey] = v.msg || null; // eslint-disable-line
        if (v.msg != null) {
          hasError = true;
        }
        if (Array.isArray(v.errors)) {
          v.errors.forEach(item => {
            let r = false;
            if (typeof item === 'string') {
              r = applyErrorState(data, item, false, isSubmit);
            } else if (item) {
              r |= applyErrorState(data, item.name, item.msg || true, isSubmit);
            }
            hasError = hasError || r;
          });
        }
      }
    }
    return hasError;
  }

  /**
   * Converts the value object into a { values : { }, errors : { }, msgs : { } }
   **/
  function dataToForm(values) {
    const nState = makeStruct();
    nState.values = {
      ...values
    };
    const e1 = updateValidatorStates(nState);
    const e2 = updateGeneralValidatorStates(nState);
    nState.hasError = e1 || e2;
    return nState;
  }
  function onSubmit(evt) {
    const data = getData();
    if (data.hasError) {
      preventDefault();
      if (options.onSubmitError) {
        options.onSubmitError(data);
      }
      return;
    }
    if (!isSubmitValid()) {
      preventDefault(evt);
      if (options.onSubmitError) {
        options.onSubmitError(data);
      }
      return;
    }
    if (options.onSubmit) {
      preventDefault(evt);
      options.onSubmit({
        value: data.values,
        event: evt
      });
    }
  }

  /**
   * Returns true if there are no immediate errors. Normally this
   * is used to enable the submit button. This only checks the
   * states of the immediate validators, not the submit validators
   */
  function isValid() {
    const fData = getData();
    if (options.isValid) {
      return options.isValid(fData);
    }
    return fData.hasError !== true;
  }
  function isSubmitValid() {
    const cData = getData();
    const data = makeStruct(cData);
    const e1 = updateValidatorStates(data, true);
    const e2 = updateGeneralValidatorStates(data, true);
    if (e1 || e2) {
      data.hasError = true;
      data.hasChanges = true;
      component.setState({
        [formKey]: data
      });
      return false;
    }
    return true;
  }

  /**
   * @return {object} an object that contains data-error : true
   * if the named component is in an error state.
   **/
  function dataProps(name) {
    const dp = {};
    if (hasError(name)) {
      dp['data-error'] = true;
    }
    return dp;
  }

  /**
   * Returns true if the the users has made
   * changes to the values
   **/
  function hasChanges() {
    return component && component.state && component.state[formKey] && component.state[formKey].hasChanges || false;
  }
  function doneChanging() {
    const nState = dataToForm(getData().values);
    component.setState({
      [formKey]: nState
    });
  }

  /**
   * Returns the value for the named form elements field.
   * @param {string} type one of 'errors', 'msgs', 'values'
   * @param {string} name the name of the attribute
   **/
  function getFromData(type, name) {
    const fd = getData();
    if (fd == null || fd[type] == null || fd[type][name] == null) {
      return null;
    }
    return fd[type][name];
  }
  function hasError(name) {
    return getFromData('errors', name) === true || getFromData('submitErrors', name) === true;
  }

  /**
   * Returns the message for the named field. If the name
   * is not supplied or is null, the overall error message
   * will be returned
   **/
  function message(name) {
    if (name == null) {
      const d = getData();
      return d.submitErrorMsg || d.errorMsg || null;
    }
    return getFromData('submitMsgs', name) || getFromData('msgs', name) || null;
  }
  function value(name) {
    return getFromData('values', name);
  }
  function revert() {
    const nState = dataToForm(currentData);
    // Remove submit errors
    nState.submitErrors = {};
    nState.submitMsgs = {};
    nState.errorMsg = null;
    component.setState({
      [formKey]: nState
    });
    if (onRevert) {
      onRevert(nState);
    }
  }
  return {
    applyErrorState,
    setComponent,
    onInputChange,
    dataProps,
    onSubmit,
    isValid,
    isSubmitValid,
    hasError,
    hasChanges,
    doneChanging,
    setDefaultData,
    getDefaultData,
    // getDefaultForm,
    message,
    value,
    validator: getValidator,
    // classnames,
    revert,
    getData,
    options
  };
}
function preventDefault(evt) {
  if (evt && typeof evt.preventDefault === 'function') {
    evt.preventDefault();
  }
}
function ask(obj, dValue, ...args) {
  if (typeof obj === 'function') {
    return obj.apply(this, args);
  }
  if (obj instanceof RegExp) {
    return obj.test(args[0]);
  }
  return dValue;
}
function longerThan(n, trimmed) {
  return v => {
    return typeof v === 'string' && (trimmed ? v.trim().length > n : v.length > n);
  };
}
export default {
  longerThan,
  create
};